import type {
  Asset,
  VideoAsset,
} from '@frameio/graphql-client/generated/types';

/**
 * Represents a document (e.g., a Premiere sequence or After Effects composition).
 */
export type Document = {
  id: string;
  name: string;
};

/**
 * Lifecycle status of a document export. Hosts that have a distinct render /
 * encode step (e.g. Premiere) advance through `Encoding` → `Uploading`; hosts
 * that don't (e.g. After Effects) start directly at `Uploading`. `Failed` and
 * `Canceled` are terminal; `Completed` items are pruned from the active list.
 */
export enum DocumentProgressStatus {
  Canceled = 'canceled',
  Completed = 'completed',
  Encoding = 'encoding',
  Failed = 'failed',
  Queued = 'queued',
  Uploading = 'uploading',
}

/**
 * Common fields for tracking the progress of a single export job.
 * Host-specific types extend this with their own identifier fields
 * (e.g., Premiere adds `sequenceId` + `fullSequence`; After Effects adds
 * `compositionId`).
 *
 * `progress` is a single 0..1 overall value — hosts that have multiple
 * phases compute the weighted overall progress at write time.
 * `failedMessage` is the failure reason text — only meaningful when
 * `status === DocumentProgressStatus.Failed`.
 */
export type DocumentProgressItem = {
  assetId?: string;
  bytesSent?: number;
  bytesTotal?: number;
  createdAt: number;
  failedMessage?: string;
  fileName: string;
  folderId?: string;
  id: string;
  jobId?: string;
  progress?: number;
  projectId?: string;
  status?: DocumentProgressStatus;
  transferBatchId?: string;
};

/**
 * A map of export progress items keyed by a host-specific identifier
 * (Premiere: batchEncoderId — one sequence per job; After Effects: assetId
 * — multiple comps per render job, so per-asset keying avoids clobbering).
 */
export type DocumentProgressItems = {
  [itemId: string]: DocumentProgressItem;
};

/**
 * Generalized state for the active document (e.g., the currently selected
 * Premiere sequence or After Effects composition). Used for host hooks
 * (e.g. `usePremiereDocumentExports`) and rich UI. The active-document card
 * shell receives routing fields as individual props from the host hook/context.
 */
export type ActiveDocumentData = {
  __typename?: string;
  accountId: string;
  assetId: string;
  assetName?: string;
  documentId?: string;
  documentName: string;
  documents?: Document[];
  exportedAt?: number;
  inProgressId: string | null;
  isLoadingAssetData: boolean;
  projectId?: string;
  projectName: string;
  shareId?: string;
  versionId?: string;
  versionNumber: number;
  versions: Asset[];
  videoStackAsset?: VideoAsset;
};

/**
 * Represents an enriched export with core properties guaranteed after
 * processing. This type represents exports after they've been processed
 * and enriched with additional fields like navigation URLs.
 */
export type EnrichedDocumentExportItem = {
  __typename?: string;
  href: string;
  exportedAt?: number | null;
  assetId?: string;
  projectId?: string;
  accountId?: string;
  versionId?: string | null;
  shareId?: string | null;
  documentId?: string | null;
};

/**
 * Represents a successfully completed document export item in a list.
 */
export type DocumentCompletedExportListItem = EnrichedDocumentExportItem & {
  key: string;
};

/**
 * Represents a document export that is currently in progress.
 */
export type DocumentInProgressExportListItem = DocumentProgressItem & {
  __typename: 'InProgressExport';
  key: string;
  assetId?: string;
  href?: string;
};

/**
 * A union type representing any possible item in the document export list.
 */
export type DocumentExportListItem =
  | DocumentCompletedExportListItem
  | DocumentInProgressExportListItem;

/**
 * Represents a group of document exports, typically grouped by date.
 */
export type GroupedDocumentExportType = {
  dateLabel: string;
  id: string;
  items: DocumentExportListItem[];
};

/**
 * Represents asset data fetched for an export item.
 */
export type AssetLike =
  | (Asset & { commentCount?: number; project?: { id: string; name: string } })
  | null
  | undefined;
